import { TextField } from '@mui/material';
import {
  Dispatch,
  useContext,
  useEffect,
  useReducer,
  useRef,
  useState,
} from 'react';
import { AuthenticatedSessionContext } from '../../authenticatedSession/AuthenticatedSessionContext';
import type {
  FirmData,
  TransactionData,
  TransactionPaymentMethod,
  TransactionType,
} from '../../database/baseTypes';
import { useFirm } from '../../database/hooks/hooks';
import { inferTransactionStatusFromCompletion } from '../../utils/transactionStatus';
import { textFieldStyles } from '../../muiStyles/textFieldStyles';
import DateTextField from '../DateTextField/DateTextField';
import MatterAutocomplete from '../MatterAutocomplete';
import AccountAutocomplete from '../../views/Financials/AccountAutocomplete';
import MethodAutocomplete from '../../views/Financials/MethodAutocomplete';
import TypeAutocomplete from '../../views/Financials/TypeAutocomplete';
import { isLegacyFromAccountType } from '../../views/Financials/Transactions/util';
import {
  type FinixEnvironment,
  useFinixEnvironment,
} from '@/app/FinixEnvironmentContext';
import { getRetainerTransferAccountFields } from '@/financials/retainerTransferAccounts';
import {
  QuickEntryModal,
  StatusMessage,
} from '../QuickEntryModal/QuickEntryModal';
import { defaultState, FormAction, formReducer, FormState } from './reducer';
import { notifications } from '@mantine/notifications';
import { createFinancialTransaction } from '@/financials/transactionMutations';

const TransactionEntryModal = ({
  fixedMatterId,
  onClose,
  onTransactionCreated,
}: {
  fixedMatterId?: string;
  onClose: () => void;
  onTransactionCreated?: () => void;
}) => {
  const authSession = useContext(AuthenticatedSessionContext);
  const [state, dispatch] = useReducer(formReducer, defaultState(undefined));
  const [statusMessage, setStatusMessage] = useState<
    StatusMessage | undefined
  >();
  const amountInputRef = useRef<HTMLInputElement>(null);
  const { firm } = useFirm();
  const finixEnvironment = useFinixEnvironment();
  const isRetainerTransfer = state.type.value === 'retainerTransfer';
  const fromAccountValueRef = useRef(state.fromAccount.value);
  const toAccountValueRef = useRef(state.toAccount.value);
  const defaultAttemptKeyRef = useRef<string | undefined>(undefined);
  fromAccountValueRef.current = state.fromAccount.value;
  toAccountValueRef.current = state.toAccount.value;

  useEffect(() => {
    setTimeout(() => {
      if (amountInputRef.current) {
        amountInputRef.current.focus();
      }
    }, 0);
  }, []);

  useEffect(() => {
    const matterId = fixedMatterId ?? state.matter.value?.id;
    if (!authSession?.db || !matterId || !isRetainerTransfer) return;

    let isCancelled = false;
    const loadRetainerTransferDefaults = async () => {
      try {
        const [firmDoc, matterDoc] = await Promise.all([
          authSession.db.getFirm(),
          authSession.db.getMatter(matterId),
        ]);
        if (isCancelled) return;

        const firm = firmDoc.data();
        const matter = matterDoc.data();
        if (!firm || !matter) return;

        const accountFields = getManualTransactionAccountDefaultFields({
          firm,
          finixEnvironment: finixEnvironment.environment,
          matterUsState: matter.state,
          type: 'retainerTransfer',
        });
        if (!fromAccountValueRef.current) {
          dispatch({
            type: 'SET_FROM_ACCOUNT_VAL',
            account: accountFields.fromInternalMerchantAccountId,
          });
        }
        if (!toAccountValueRef.current) {
          dispatch({
            type: 'SET_TO_ACCOUNT_VAL',
            account: accountFields.toInternalMerchantAccountId,
          });
        }
      } catch (error) {
        console.error(
          'Error loading retainer transfer account defaults:',
          error,
        );
      }
    };

    loadRetainerTransferDefaults();
    return () => {
      isCancelled = true;
    };
  }, [
    authSession?.db,
    finixEnvironment.environment,
    fixedMatterId,
    isRetainerTransfer,
    state.matter.value?.id,
  ]);

  useEffect(() => {
    if (!firm || !state.type.value || state.type.value === 'retainerTransfer') {
      if (!state.type.value || state.type.value === 'retainerTransfer') {
        defaultAttemptKeyRef.current = undefined;
      }
      return;
    }
    const defaultAttemptKey = [
      state.type.value,
      finixEnvironment.environment,
      firm.defaultOperatingAccountId,
      firm.defaultSandboxOperatingAccountId,
      firm.defaultTrustAccountId,
      firm.defaultSandboxTrustAccountId,
    ].join(':');
    if (defaultAttemptKeyRef.current === defaultAttemptKey) return;

    const accountFields = getManualTransactionAccountDefaultFields({
      firm,
      finixEnvironment: finixEnvironment.environment,
      type: state.type.value,
    });

    if (
      !fromAccountValueRef.current &&
      accountFields.fromInternalMerchantAccountId
    ) {
      dispatch({
        type: 'SET_FROM_ACCOUNT_VAL',
        account: accountFields.fromInternalMerchantAccountId,
      });
    }
    if (
      !toAccountValueRef.current &&
      accountFields.toInternalMerchantAccountId
    ) {
      dispatch({
        type: 'SET_TO_ACCOUNT_VAL',
        account: accountFields.toInternalMerchantAccountId,
      });
    }
    defaultAttemptKeyRef.current = defaultAttemptKey;
  }, [finixEnvironment.environment, firm, state.type.value]);

  const handleSave = async () => {
    if (!authSession) return;

    const showErrorNotification = () => {
      notifications.show({
        title: 'Error',
        message: 'All fields are required',
        color: 'red.4',
        autoClose: 3000,
      });
    };

    const validationResult = validateStateFields({
      state,
      dispatch,
      fixedMatterId,
    });

    if (!validationResult.valid) {
      showErrorNotification();
      return;
    }

    const newTransaction = buildManualTransaction(validationResult.data);

    try {
      await createFinancialTransaction(newTransaction);

      notifications.show({
        title: 'Transaction Created ✨',
        message: `New transaction for $${state.amount.value} created successfully`,
        color: 'lime.4',
        autoClose: 3000,
      });

      onTransactionCreated?.();
      onClose();
    } catch (error) {
      console.error('Error creating transaction:', error);
      notifications.show({
        title: 'Error',
        message: 'Failed to create transaction',
        color: 'red.4',
        autoClose: 3000,
      });
    }
  };

  return (
    <QuickEntryModal
      title='New Transaction'
      statusMessage={statusMessage}
      onClose={onClose}
      onSave={handleSave}
    >
      <TextField
        sx={textFieldStyles}
        label='$ Amount'
        variant='outlined'
        type='number'
        fullWidth
        value={state.amount.value || ''}
        onChange={(e) => {
          const value = e.target.value;
          if (value === '' || !isNaN(parseFloat(value))) {
            dispatch({
              type: 'SET_AMOUNT_VAL',
              amount: parseFloat(value) || 0,
            });
          }
        }}
        onBlur={(e) => {
          if (state.amount.value) {
            dispatch({
              type: 'SET_AMOUNT_VAL',
              amount: Math.round(state.amount.value * 100) / 100,
            });
          }
        }}
        error={!!state.amount.error}
        inputRef={amountInputRef}
        required
        autoFocus
        placeholder='0.00'
        inputProps={{
          step: '0.01',
          min: '0',
        }}
      />
      {!fixedMatterId && (
        <MatterAutocomplete
          firmDb={authSession?.db}
          error={!!state.matter.error}
          value={state.matter.value}
          onSelect={(matter) => dispatch({ type: 'SET_MATTER_VAL', matter })}
        />
      )}
      <DateTextField
        value={state.date.value ?? null}
        error={!!state.date.error}
        required={true}
        onChange={(value) =>
          dispatch({ type: 'SET_DATE_VAL', date: value ?? undefined })
        }
      />
      <TypeAutocomplete
        error={!!state.type.error}
        value={state.type.value}
        onSelect={(typeVal) =>
          dispatch({ type: 'SET_TYPE_VAL', typeValue: typeVal })
        }
      />
      <AccountAutocomplete
        firmDb={authSession?.db}
        label='From Account'
        error={!!state.fromAccount.error}
        helperText={state.fromAccount.error}
        value={state.fromAccount.value}
        required={false}
        onSelect={(account) =>
          dispatch({ type: 'SET_FROM_ACCOUNT_VAL', account })
        }
      />
      <AccountAutocomplete
        firmDb={authSession?.db}
        label='To Account'
        error={!!state.toAccount.error}
        helperText={state.toAccount.error}
        value={state.toAccount.value}
        required={false}
        onSelect={(account) =>
          dispatch({ type: 'SET_TO_ACCOUNT_VAL', account })
        }
      />
      <MethodAutocomplete
        error={!!state.paymentMethod.error}
        value={state.paymentMethod.value}
        onSelect={(method) =>
          dispatch({ type: 'SET_METHOD_VAL', paymentMethod: method })
        }
      />
    </QuickEntryModal>
  );
};

export default TransactionEntryModal;

export interface ValidatedManualTransactionForm {
  date: Date;
  amount: number;
  matter: string;
  fromAccount?: string;
  toAccount?: string;
  paymentMethod: TransactionPaymentMethod;
  type: TransactionType;
}

export const buildManualTransaction = (
  data: ValidatedManualTransactionForm,
): TransactionData => {
  const completedTimestamp = new Date(data.date);
  const internalMerchantAccountId = getLegacyInternalMerchantAccountId(data);
  return {
    amount: data.amount,
    matter: data.matter,
    createdTimestamp: new Date(data.date),
    completedTimestamp,
    ...(internalMerchantAccountId ? { internalMerchantAccountId } : {}),
    ...(data.fromAccount
      ? { fromInternalMerchantAccountId: data.fromAccount }
      : {}),
    ...(data.toAccount ? { toInternalMerchantAccountId: data.toAccount } : {}),
    paymentMethod: data.paymentMethod,
    type: data.type,
    source: 'directInput',
    status: inferTransactionStatusFromCompletion(completedTimestamp),
  };
};

const getLegacyInternalMerchantAccountId = (
  data: Pick<
    ValidatedManualTransactionForm,
    'fromAccount' | 'toAccount' | 'type'
  >,
): string | undefined => {
  if (data.type === 'retainerTransfer') {
    return data.toAccount;
  }
  if (isLegacyFromAccountType(data.type)) {
    return data.fromAccount ?? data.toAccount;
  }
  return data.toAccount ?? data.fromAccount;
};

export const getManualTransactionAccountDefaultFields = ({
  firm,
  finixEnvironment,
  matterUsState,
  type,
}: {
  firm: FirmData;
  finixEnvironment: FinixEnvironment;
  matterUsState?: string;
  type: TransactionType;
}): {
  fromInternalMerchantAccountId?: string;
  toInternalMerchantAccountId?: string;
} => {
  if (type === 'retainerTransfer' && matterUsState) {
    return getRetainerTransferAccountFields({
      firm,
      finixEnvironment,
      matterUsState,
    });
  }

  const defaultOperatingAccountId = getDefaultMerchantAccountId({
    firm,
    finixEnvironment,
    operatingOrTrust: 'operating',
  });
  const defaultTrustAccountId = getDefaultMerchantAccountId({
    firm,
    finixEnvironment,
    operatingOrTrust: 'trust',
  });

  if (type === 'refundFromOperating' || type === 'dispute') {
    return { fromInternalMerchantAccountId: defaultOperatingAccountId };
  }
  if (type === 'refundFromTrust') {
    return { fromInternalMerchantAccountId: defaultTrustAccountId };
  }
  if (type === 'retainerDeposit') {
    return { toInternalMerchantAccountId: defaultTrustAccountId };
  }
  return { toInternalMerchantAccountId: defaultOperatingAccountId };
};

const getDefaultMerchantAccountId = ({
  firm,
  finixEnvironment,
  operatingOrTrust,
}: {
  firm: FirmData;
  finixEnvironment: FinixEnvironment;
  operatingOrTrust: 'operating' | 'trust';
}): string | undefined => {
  const defaultAccountId =
    operatingOrTrust === 'operating'
      ? finixEnvironment === 'sandbox'
        ? firm.defaultSandboxOperatingAccountId
        : firm.defaultOperatingAccountId
      : finixEnvironment === 'sandbox'
        ? firm.defaultSandboxTrustAccountId
        : firm.defaultTrustAccountId;

  return firm.merchantAccounts.find(
    (account) =>
      account.id === defaultAccountId &&
      account.operatingOrTrust === operatingOrTrust &&
      account.isSandbox === (finixEnvironment === 'sandbox'),
  )?.id;
};

const validateStateFields = (props: {
  state: FormState;
  dispatch: Dispatch<FormAction>;
  fixedMatterId: string | undefined;
}):
  | {
      valid: true;
      data: ValidatedManualTransactionForm;
    }
  | { valid: false } => {
  const { state, dispatch, fixedMatterId } = props;
  let hasError = false;
  if (!state.date.value) {
    dispatch({ type: 'SET_DATE_ERROR', error: 'Required' });
    hasError = true;
  }
  if (!fixedMatterId && !state.matter.value) {
    dispatch({ type: 'SET_MATTER_ERROR', error: 'Required' });
    hasError = true;
  }
  if (!state.amount.value) {
    dispatch({ type: 'SET_AMOUNT_ERROR', error: 'Required' });
    hasError = true;
  }
  if (!state.fromAccount.value && !state.toAccount.value) {
    dispatch({
      type: 'SET_FROM_ACCOUNT_ERROR',
      error: 'Select at least one account',
    });
    dispatch({
      type: 'SET_TO_ACCOUNT_ERROR',
      error: 'Select at least one account',
    });
    hasError = true;
  }
  if (!state.paymentMethod.value) {
    dispatch({ type: 'SET_METHOD_ERROR', error: 'Required' });
    hasError = true;
  }
  if (!state.type.value) {
    dispatch({ type: 'SET_TYPE_ERROR', error: 'Required' });
    hasError = true;
  }
  if (hasError) {
    return { valid: false };
  }
  return {
    valid: true,
    data: {
      date: state.date.value!,
      amount: state.amount.value!,
      matter: fixedMatterId ?? state.matter.value!.id,
      fromAccount: state.fromAccount.value,
      toAccount: state.toAccount.value,
      paymentMethod: state.paymentMethod.value!,
      type: state.type.value!,
    },
  };
};
